import { useCallback, useEffect, useState } from "react";
import { Toast } from "primereact/toast";

import useToastActionWrapper from "./useToastActionWrapper";
import {
  createVendorConfig,
  listVendorConfigs,
  updateVendorConfig,
  uploadVendorAsset,
} from "../services/onboarding/vendorConfigs";
import {
  CreateVendorConfigPayload,
  UpdateVendorConfigPayload,
  VendorAssetType,
  VendorConfig,
} from "../types/onboarding";

interface UseVendorConfigs {
  configs: VendorConfig[];
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  create: (payload: CreateVendorConfigPayload) => Promise<boolean>;
  update: (
    vendorId: string,
    payload: UpdateVendorConfigPayload
  ) => Promise<boolean>;
  upload: (
    vendorId: string,
    assetType: VendorAssetType,
    file: File
  ) => Promise<boolean>;
}

/**
 * The manufacturer configuration catalogue (CWA-91) — what a customer receives
 * with their configuration email, per wallbox manufacturer.
 *
 * Every mutation reloads the list from the server, so a failed upload leaves the
 * previously stored file visibly intact rather than showing an optimistic lie.
 */
const useVendorConfigs = (toastRef: React.RefObject<Toast>): UseVendorConfigs => {
  const { toastActionWrapper } = useToastActionWrapper(toastRef);
  const [configs, setConfigs] = useState<VendorConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setConfigs(await listVendorConfigs());
    } catch (e) {
      // No toast here — the page renders an inline error for a failed load, and
      // reporting it twice just adds noise. Mutations still toast.
      console.error("Failed to load manufacturer configurations", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // create/update/upload return a boolean so a dialog can stay open on failure.
  const create = useCallback(
    async (payload: CreateVendorConfigPayload): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          await createVendorConfig(payload);
          await reload();
          ok = true;
          return true;
        },
        successMessage: "Manufacturer added.",
        errorMessage: "The manufacturer could not be added.",
      });
      return ok;
    },
    [reload, toastActionWrapper]
  );

  const update = useCallback(
    async (
      vendorId: string,
      payload: UpdateVendorConfigPayload
    ): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          await updateVendorConfig(vendorId, payload);
          await reload();
          ok = true;
          return true;
        },
        successMessage: "Manufacturer updated.",
        errorMessage: "The manufacturer could not be updated.",
      });
      return ok;
    },
    [reload, toastActionWrapper]
  );

  const upload = useCallback(
    async (
      vendorId: string,
      assetType: VendorAssetType,
      file: File
    ): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          await uploadVendorAsset(vendorId, assetType, file);
          await reload();
          ok = true;
          return true;
        },
        successMessage: `${file.name} uploaded.`,
        errorMessage: `${file.name} could not be uploaded. The previous file is unchanged.`,
      });
      return ok;
    },
    [reload, toastActionWrapper]
  );

  return { configs, loading, error, reload, create, update, upload };
};

export default useVendorConfigs;
